import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParamsModal } from '../ParamsModal';
import { CurrentResidenceQuery } from '../../../../graphql/graphql';
import {
  ParamsContainer,
  ParamsItem,
  ParamsItemTitle,
  buttonStyles,
  ParamsSectionContainer
} from './styled';
import { Typography } from '../../../ui/Typography';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';
import { ResidenceSection } from '../ResidenceSection';
import { prettyNumber } from '../../../../utils/helpers';

interface ParamsSectionProps {
  residenceData: Partial<NonNullable<CurrentResidenceQuery['residence']>>;
}

export const ParamsSection: React.FC<ParamsSectionProps> = memo(
  ({ residenceData }) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const isClient = useClient();
    const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);

    const onButtonClick = () => {
      setIsParamsModalOpen(true);
    };

    const {
      deadline,
      buildingClass,
      buildingsCount,
      blocks,
      apartmentsCount,
      liftsPerBlock,
      floorsPerBuildingSplitted = [],
      apartmentsPerFloorSplitted = [],
      technology,
      decoration,
      ceilingHeight,
      windowType,
      layout
    } = residenceData;

    const infoItems = useMemo(
      () => [
        {
          key: 'deadline',
          label: t('residences.deadline'),
          value: deadline
        },
        {
          key: 'buildingClass',
          label: t('residences.class'),
          value: buildingClass?.name
        },
        {
          key: 'buildingsCount',
          label: t('residences.buildings_count_param'),
          value: buildingsCount
        },
        {
          key: 'blocks',
          label: t('residences.blocks_count'),
          value: blocks
        },
        {
          key: 'apartmentsCount',
          label: t('residences.apartments_count'),
          value: prettyNumber(apartmentsCount)
        },
        {
          key: 'liftsPerBlock',
          label: t('residences.lifts_per_block'),
          value: liftsPerBlock
        },
        ...floorsPerBuildingSplitted.map((flor, index) => ({
          key: `floorsPerBuilding-${index}`,
          label: `${t(
            'residences.floors_per_building'
          )}${floorsPerBuildingSplitted.length > 1 ? ` ${index + 1}` : ''}`,
          value: flor
        })),
        ...apartmentsPerFloorSplitted.map((apartment, index) => ({
          key: `apartmentsPerFloor-${index}`,
          label:
            apartmentsPerFloorSplitted.length === 1
              ? t('residences.apartments_per_floor_single')
              : t('residences.apartments_per_floor', { count: index + 1 }),
          value: apartment
        }))
      ],
      [
        t,
        deadline,
        buildingClass,
        buildingsCount,
        blocks,
        apartmentsCount,
        liftsPerBlock,
        floorsPerBuildingSplitted,
        apartmentsPerFloorSplitted
      ]
    );

    const visibleInfoItems = useMemo(() => {
      const filtered = infoItems.filter(item => item.value);

      if (!isSmallOrLargeDesktop && isClient) {
        return filtered.slice(0, 6);
      }

      return filtered.slice(0, 8);
    }, [infoItems, isSmallOrLargeDesktop, isClient]);

    const isButtonShown =
      !!residenceData.facilities?.length ||
      technology ||
      decoration ||
      ceilingHeight ||
      windowType ||
      layout;

    return (
      <>
        <ResidenceSection title={t('residences.params_modal_content')}>
          <ParamsSectionContainer>
            <ParamsContainer data-cy="residence-params-section">
              {visibleInfoItems.map(({ key, label, value }) => (
                <ParamsItem key={key} data-cy="residence-param">
                  <ParamsItemTitle tag="body_1" data-cy="residence-param-title">
                    {label}
                  </ParamsItemTitle>
                  <Typography
                    tag="title_3_desc"
                    $lineToShow={1}
                    data-cy="residence-param-value"
                  >
                    {value}
                  </Typography>
                </ParamsItem>
              ))}
            </ParamsContainer>

            {isButtonShown && (
              <Button
                text={t('residences.all_params')}
                stat="residence-params"
                cypress="residence-all-params-button"
                onClick={onButtonClick}
                state={ButtonType.edit}
                styles={buttonStyles}
              />
            )}
          </ParamsSectionContainer>
        </ResidenceSection>
        {isParamsModalOpen && (
          <ParamsModal
            isOpen={isParamsModalOpen}
            onClose={() => setIsParamsModalOpen(false)}
            residenceData={residenceData}
            infoItems={infoItems}
          />
        )}
      </>
    );
  }
);

ParamsSection.displayName = `ParamsSection`;
